import posthog from 'posthog-js';

/**
 * PostHog — product analytics, heatmaps, and session replay (browser SDK).
 *
 * Privacy posture (this is a health/fitness app — recordings and autocapture can
 * expose client PII: names, health metrics, messages, Oura data):
 *   - Session replay masks ALL inputs and ALL text by default
 *     (`maskAllInputs` + `maskTextSelector: '*'`). Recordings show layout/clicks,
 *     not readable content. Add the class `ph-no-capture` to any element that must
 *     never be recorded OR autocaptured (chat, client health, Oura, emails).
 *   - Only "identified" person profiles are created — no anonymous PII profiles.
 *
 * CONSENT (action item before EU users / public launch):
 *   This currently begins capturing on load. For GDPR-style consent, init with
 *   `opt_out_capturing_by_default: true` and call `posthog.opt_in_capturing()`
 *   after the user accepts (and `posthog.opt_out_capturing()` if they decline).
 *
 * Config via build-time env (browser-exposed VITE_ vars), set in Cloudflare
 * Pages env for prod and `.env.local` for local dev:
 *   VITE_PUBLIC_POSTHOG_KEY   = phc_...                     (project key)
 *   VITE_PUBLIC_POSTHOG_HOST  = https://us.i.posthog.com    (US ingest host)
 */

let initialized = false;

// Element attributes that can carry free text / client PII (e.g. aria-label
// "Message Jennie Chan"). Heatmaps match on tag/class/position, not these.
const PII_ELEMENT_ATTRS = ['attr__aria-label', 'attr__title', 'attr__alt', 'attr__placeholder', 'attr__value'];

/**
 * Strip free text from autocaptured elements before any event leaves the browser.
 *
 * Autocapture records the text + attributes of clicked elements — on a health app
 * that means client names can land in PostHog (e.g. `clicked p with text "Jennie
 * Chan"`). Heatmaps/clickmaps match on tag/class/position, NOT text, so removing it
 * costs nothing for analytics while guaranteeing client PII never leaves the device.
 * Runs on every event via `before_send`; wrapped so a scrub bug can never drop or
 * break capture. Custom events (message_sent, etc.) have no $elements → no-op.
 */
function scrubElementPII(event: any): any {
  try {
    const p = event?.properties;
    if (!p) return event;
    if (Array.isArray(p.$elements)) {
      p.$elements = p.$elements.map((el: any) => {
        if (!el || typeof el !== 'object') return el;
        const cleaned = { ...el };
        delete cleaned.$el_text;
        delete cleaned.text;
        for (const k of PII_ELEMENT_ATTRS) delete cleaned[k];
        return cleaned;
      });
    }
    // $elements_chain is a serialized string that embeds text="..."; blank it out
    // but keep the chain structure so element matching for heatmaps still works.
    if (typeof p.$elements_chain === 'string') {
      p.$elements_chain = p.$elements_chain.replace(/text="[^"]*"/g, 'text=""');
    }
    delete p.$el_text;
  } catch {
    /* never let scrubbing break capture */
  }
  return event;
}

export function initPostHog(): void {
  if (initialized) return;

  const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string | undefined;
  const host = import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined;

  // Only run in production builds with a configured key, so local/dev sessions
  // don't pollute prod analytics. To test locally, set the VITE_ vars in
  // .env.local and temporarily drop the `import.meta.env.PROD` guard.
  if (!key || !host || !import.meta.env.PROD) return;

  // Defensive: PostHog must never be able to crash app startup. initPostHog runs
  // at module load, before React mounts — an unhandled throw here would white-
  // screen the app. Swallow any SDK/config failure; analytics is non-critical.
  try {
    posthog.init(key, {
      api_host: host,
      person_profiles: 'identified_only',
      autocapture: true, // powers heatmaps (click / rage-click / dead-click maps)
      capture_pageview: true,
      capture_pageleave: true,
      before_send: scrubElementPII, // strip client PII from autocapture text
      // Consent: capture NOTHING (no events, autocapture, or replay) until the
      // user explicitly opts in via the consent banner. See grantPostHogConsent.
      opt_out_capturing_by_default: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*', // strongest masking — redact all text in replays (health PII)
      },
    });
    initialized = true;
    // Re-apply the user's stored choice on load (our flag is the source of truth),
    // guarding against drift between our record and posthog's internal opt state.
    if (readConsentFlag() === 'granted') {
      try {
        posthog.opt_in_capturing();
      } catch (err) {
        console.warn('[posthog] re-opt-in failed', err);
      }
    }
  } catch (err) {
    console.warn('[posthog] init failed; analytics disabled', err);
  }
}

/**
 * Tie the current session to the signed-in coach so funnels, retention, and
 * replays attribute to a real user instead of an anonymous device ID.
 * Call on login / when auth state resolves. No-ops if PostHog isn't active.
 */
export function identifyPostHog(user: { id: string; email?: string }): void {
  if (!initialized || !user?.id) return;
  try {
    posthog.identify(user.id, user.email ? { email: user.email } : undefined);
  } catch (err) {
    console.warn('[posthog] identify failed', err);
  }
}

/**
 * Clear the identified user (call on logout) so the next person on a shared
 * device isn't merged into the previous coach's profile.
 */
export function resetPostHog(): void {
  if (!initialized) return;
  try {
    posthog.reset();
  } catch (err) {
    console.warn('[posthog] reset failed', err);
  }
}

/**
 * Capture a product event. No-ops when PostHog isn't active (dev / no key).
 *
 * IMPORTANT: keep client PII out of `properties` — no names, emails, health
 * metrics, or message content. Use ids and counts only (e.g. clientId,
 * programId, plan, weekCount). Same reasoning as the replay masking.
 */
export function capturePostHog(event: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  try {
    posthog.capture(event, properties);
  } catch (err) {
    console.warn('[posthog] capture failed', err);
  }
}

// ── Consent (opt-in for everyone) ──────────────────────────────────────────
// PostHog is initialized but captures nothing until the user opts in. We record
// the EXPLICIT choice in our own localStorage key — we must NOT rely on
// posthog.has_opted_out_capturing(), because with `opt_out_capturing_by_default`
// it returns true for every user who hasn't chosen yet, which is
// indistinguishable from an explicit "decline" and suppresses the banner
// forever (so nobody can ever opt in). Our flag distinguishes "no choice yet".

export type ConsentState = 'granted' | 'denied' | 'unknown';

const CONSENT_KEY = 'coachd_ph_consent'; // 'granted' | 'denied'

function readConsentFlag(): 'granted' | 'denied' | null {
  try {
    const v = window.localStorage.getItem(CONSENT_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null;
  }
}

function writeConsentFlag(value: 'granted' | 'denied'): void {
  try {
    window.localStorage.setItem(CONSENT_KEY, value);
  } catch {
    /* localStorage may be unavailable (private mode / quota) */
  }
}

/** True when PostHog actually initialized (prod build + key). */
export function isPostHogActive(): boolean {
  return initialized;
}

/** Current consent: 'unknown' = no explicit choice yet → show the banner. */
export function getPostHogConsent(): ConsentState {
  if (!initialized) return 'unknown';
  return readConsentFlag() ?? 'unknown';
}

/** User accepted — start capturing (events, autocapture, replay). Persisted. */
export function grantPostHogConsent(): void {
  if (!initialized) return;
  writeConsentFlag('granted');
  try {
    posthog.opt_in_capturing();
  } catch (err) {
    console.warn('[posthog] opt-in failed', err);
  }
}

/** User declined — stay off. Persisted, so they aren't asked again. */
export function denyPostHogConsent(): void {
  if (!initialized) return;
  writeConsentFlag('denied');
  try {
    posthog.opt_out_capturing();
  } catch (err) {
    console.warn('[posthog] opt-out failed', err);
  }
}

export { posthog };
